import { Button, Icon, IconButton, VerticalDivider } from '@smartgift/frontend-toolkit';
import { useQueryClient } from '@tanstack/react-query';
import { useFormik } from 'formik';
import { AnimatePresence } from 'framer-motion';
import merge from 'lodash/merge';
import PropTypes from 'prop-types';
import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDebounce } from 'use-debounce';
import * as yup from 'yup';

import { AppLoader } from '@hero/components/atoms';
import { MAX_SEARCH_LENGTH, MIN_LENGTH, QUERY_KEYS } from '@hero/constants';
import { useOnClickOutside, useQueries } from '@hero/hooks';
import { productServices } from '@hero/services';
import { useSearchInfiniteProducts } from '@hero/services/product-service/hooks';
import { useNotification, useShoppingCart } from '@hero/store/hooks';
import { createQueryKey } from '@hero/utils';

import { ProductListItemDetail } from '../Product/Detail';
import VerticalListItem from '../Product/VerticalList/Item';
import { HorizontalProductCardSkeleton } from '../Product/VerticalList/VerticalProductCardSkeleton';

import { SearchList } from './SearchList';
import * as S from './style';

const validationSchema = yup.object().shape({
  searchTerm: yup.string().min(MIN_LENGTH).max(MAX_SEARCH_LENGTH).required()
});

const EmptySearchResult = ({ searchIsSuccess, title, description }) => (
  <S.EmptyResult>
    {searchIsSuccess && (
      <>
        <S.EmptyResultTitle variant="subtitle3Bold">{title}</S.EmptyResultTitle>
        <S.EmptyResultDescription variant="body3">{description}</S.EmptyResultDescription>
      </>
    )}
  </S.EmptyResult>
);

function ProductSearch({ selectedProducts, updateProducts, featureType, isDraft }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { queries, addQueries, removeQuery } = useQueries();
  const { searchTerm = '' } = queries;
  const { addSearch } = useShoppingCart();
  const { showInfo } = useNotification();

  const [modalProduct, setModalProduct] = useState();
  const [productIsFetching, setProductIsFetching] = useState();
  const [isFocused, setIsFocused] = useState(false);
  const [searchInputRef, setSearchInputRef] = useState();

  const formik = useFormik({
    initialValues: {
      searchTerm
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async ({ searchTerm }) => {
      addSearch(searchTerm);
      addQueries({ searchTerm });
      setIsResultsOpen(false);
    }
  });

  const hasSearchTerm = useMemo(() => !!formik.values.searchTerm?.length, [formik]);
  const [isSearchListOpen, setIsSearchListOpen] = useState(false);
  const [isResultsOpen, setIsResultsOpen] = useState(false);

  const searchListRef = useOnClickOutside(() => {
    if (isSearchListOpen) {
      setIsSearchListOpen(false);
    }
  });
  const resultsRef = useOnClickOutside(() => {
    setIsResultsOpen(false);
  });

  const handleSetSearchByRecent = value => {
    setIsSearchListOpen(false);
    setIsResultsOpen(false);
    addQueries({ searchTerm: value });
  };

  const [debouncedSearchTerm] = useDebounce(formik.values.searchTerm, 500);

  const {
    data: searchData,
    isFetching: searchIsFetching,
    isSuccess: searchIsSuccess
  } = useSearchInfiniteProducts(
    {
      filters: {
        searchTerm: debouncedSearchTerm
      }
    },
    {
      enabled: !!debouncedSearchTerm
    }
  );

  const hasNextPage = !!(
    searchData?.pages?.[searchData?.pages.length - 1].hasNextPage ||
    searchData?.pages?.[searchData?.pages.length - 1].pagination.hasNextPage
  );

  const handleShowProductModal = async p => {
    setProductIsFetching(true);
    try {
      const products = await queryClient.fetchQuery(
        createQueryKey(QUERY_KEYS.useGetProductsByCodesKey, {
          filters: { productCodes: [p.merchantProductId] }
        }),
        () =>
          productServices.searchProducts({
            filters: {
              productCodes: [p.merchantProductId]
            }
          })
      );

      const product = products?.docs?.[0];

      if (!product) {
        showInfo({
          message: t('productFetchingProblem'),
          type: 'toast'
        });
      } else {
        if (!product) {
          return null;
        }

        const selectedProduct = selectedProducts.find(
          p => p.merchantProductId === product?.merchantProductId
        );

        const mergedProduct = merge(product, selectedProduct || {});
        setModalProduct({
          product: mergedProduct,
          productWithSkuSelection: {
            ...mergedProduct,
            skus: selectedProduct?.skus || []
          },
          isSelected: !!selectedProduct
        });
      }
    } finally {
      setProductIsFetching(false);
    }
  };

  const handleHideProductModal = () => {
    setModalProduct();
  };

  const onSelectProduct = product => {
    const isSelected =
      selectedProducts.findIndex(item => item.merchantProductId === product.merchantProductId) >= 0;

    if (isSelected) {
      updateProducts(
        selectedProducts.filter(item => item.merchantProductId !== product.merchantProductId)
      );
    } else {
      updateProducts([...selectedProducts, product]);
    }

    navigate({
      search: ''
    });

    setModalProduct();
  };

  const hasSearchResults = searchData?.pages?.[0]?.docs?.length > 0;

  const isRenderSearchResultsShown = useMemo(
    () => isResultsOpen && hasSearchTerm,
    [hasSearchTerm, isResultsOpen, isSearchListOpen]
  );

  const renderSearchResults = useMemo(() => {
    return (
      isRenderSearchResultsShown && (
        <S.Results ref={resultsRef}>
          {hasSearchResults || searchIsFetching ? (
            <S.ResultsContent>
              {(hasSearchResults || !searchIsFetching) && (
                <S.ResultsItems>
                  {searchData?.pages.map((page, index) => (
                    <React.Fragment key={`fragment_${index.toString()}`}>
                      {page?.docs?.map((product, i) => {
                        const isSelected = selectedProducts.some(
                          p => p.merchantProductId === product.merchantProductId
                        );
                        return (
                          <VerticalListItem
                            key={`vertical_item_${i.toString()}`}
                            product={product}
                            onClick={() => handleShowProductModal(product)}
                            isSelected={isSelected}
                          />
                        );
                      })}
                    </React.Fragment>
                  ))}
                </S.ResultsItems>
              )}
              {((hasSearchResults && searchIsFetching) ||
                (!hasSearchResults && searchIsFetching)) && (
                <S.ResultsItems>
                  <HorizontalProductCardSkeleton count={8} />
                </S.ResultsItems>
              )}
            </S.ResultsContent>
          ) : (
            <S.ResultsContent>
              <EmptySearchResult
                searchIsSuccess={searchIsSuccess}
                title={t('thereIsNoResult')}
                description={t('thereIsNoResultDescription')}
              />
            </S.ResultsContent>
          )}
          <S.ResultsPanel>
            <VerticalDivider />
            <S.ResultsMenu>
              <S.ResultsMenuHeader>
                {t('searchForName', { name: formik.values.searchTerm })}
              </S.ResultsMenuHeader>
              <Button variant="outlined" type="button" onClick={formik.handleSubmit}>
                {t('search')}
              </Button>
            </S.ResultsMenu>
          </S.ResultsPanel>
        </S.Results>
      )
    );
  }, [
    isResultsOpen,
    isSearchListOpen,
    hasSearchResults,
    searchIsFetching,
    hasNextPage,
    searchData,
    selectedProducts,
    hasSearchTerm
  ]);

  const handleResetSearch = () => {
    formik.setFieldValue('searchTerm', '');
    removeQuery('searchTerm');
    setIsFocused(false);
  };

  return (
    <S.ProductSearchWrapper>
      <AnimatePresence initial={false}>
        {isFocused ? (
          <S.ProductSearch
            initial={{ opacity: 0, left: 'unset' }}
            animate={{ opacity: 1, left: '16px' }}
            exit={{ opacity: 0, left: 'unset' }}
            onSubmit={e => e.preventDefault()}
          >
            <S.InputArea>
              <S.InputBackButton
                variant="text"
                onClick={() => {
                  setIsFocused(false);
                  setIsSearchListOpen(false);
                  setIsResultsOpen(false);
                }}
                color="secondary"
                type="button"
              >
                <Icon name="left-chevron" size="lg" />
              </S.InputBackButton>
              <S.SearchInput
                ref={setSearchInputRef}
                width="100%"
                onClick={e => {
                  setIsSearchListOpen(true);
                  if (hasSearchTerm) {
                    setIsResultsOpen(true);
                  }
                  if (!e.target.value) {
                    removeQuery('searchTerm');
                    formik.setFieldValue('searchTerm', '');
                  }
                }}
                name="searchTerm"
                placeholder={t('searchByProduct')}
                type="text"
                value={formik.values.searchTerm}
                onChange={e => {
                  formik.handleChange(e);
                  setIsResultsOpen(true);
                }}
                onBlur={e => {
                  if (!e.target.value) {
                    handleResetSearch();
                  }
                }}
                inputProps={{
                  maxLength: MAX_SEARCH_LENGTH
                }}
                autoComplete="off"
                endIcon={
                  hasSearchTerm ? (
                    <S.ProductSearchButton onClick={handleResetSearch} type="button">
                      <Icon name="close" size="lg" />
                    </S.ProductSearchButton>
                  ) : (
                    <S.ProductSearchButton type="button">
                      <Icon name="search" size="lg" />
                    </S.ProductSearchButton>
                  )
                }
              />
            </S.InputArea>

            {isSearchListOpen && !hasSearchTerm && (
              <SearchList
                ref={searchListRef}
                onSearchByRecent={handleSetSearchByRecent}
                setIsSearchListOpen={setIsSearchListOpen}
              />
            )}

            {renderSearchResults}

            {!!modalProduct?.product && (
              <ProductListItemDetail
                isOpen={!!modalProduct?.product}
                onRequestClose={handleHideProductModal}
                okText={t('includeThisGift')}
                onOk={onSelectProduct}
                featureType={featureType}
                isDraft={isDraft}
                product={modalProduct.product}
                isSelectedProduct={modalProduct?.isSelected}
                initialSelectedSkus={modalProduct?.productWithSkuSelection?.skus}
                onUpdateProduct={product => {
                  updateProducts(
                    selectedProducts?.map(item => {
                      if (item.merchantProductId === product.merchantProductId) {
                        return product;
                      }
                      return item;
                    })
                  );
                }}
              />
            )}

            {productIsFetching && <AppLoader />}
          </S.ProductSearch>
        ) : null}
      </AnimatePresence>
      <IconButton
        variant="text"
        color="secondary"
        onClick={() => {
          setIsFocused(true);
          setTimeout(() => {
            searchInputRef?.focus();
          }, 100);
        }}
      >
        <Icon name="search" size="lg" />
      </IconButton>
    </S.ProductSearchWrapper>
  );
}

ProductSearch.propTypes = {
  selectedProducts: PropTypes.array,
  updateProducts: PropTypes.func,
  featureType: PropTypes.string,
  isDraft: PropTypes.bool
};

EmptySearchResult.propTypes = {
  searchIsSuccess: PropTypes.bool,
  title: PropTypes.node,
  description: PropTypes.node
};

export { ProductSearch };
